import { SYNC_DB_FOLDER_NAME } from "constants/settings.constants";
import { ForecastMode, ICheckedForecast } from "store/features/app/appSlice";
import { SPECIAL_FOLDERS_ID } from "store/features/arps/arpsSlice";

/**
 * Extracts unique user folder names from checked forecast items.
 * This handles both fully checked folders and partially checked folders (individual forecasts).
 *
 * When comparing "virtual" folders (same folderId but different reserveCategory),
 * the reserve category is appended to the folder name to distinguish them.
 *
 * @param checkedItems - Array of checked forecast items
 * @param treeData - Array of tree nodes containing folder information
 * @returns Array of unique folder names (with reserve category appended for virtual folders)
 */
export function getUserFolderNamesFromCheckedItems(
  checkedItems: ICheckedForecast[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  treeData: any[]
): string[] {
  if (!checkedItems || checkedItems.length === 0) {
    return [];
  }

  // Filter out special folders and items without folderId
  const validItems = checkedItems.filter(
    (item) => item.folderId && !SPECIAL_FOLDERS_ID.includes(item.folderId)
  );

  const checkedFolderItems = validItems.filter(
    (item) => item.isFolder || item.type === "folder"
  );

  // Group checked folder items by folderId to detect "virtual" folders
  const folderIdToRescats = new Map<string, Set<string>>();
  checkedFolderItems.forEach((item) => {
    const rescats = folderIdToRescats.get(item.folderId) || new Set<string>();
    if (item.reserveCategory) {
      rescats.add(item.reserveCategory);
    }
    folderIdToRescats.set(item.folderId, rescats);
  });

  // Check if we have virtual folders (same folderId with multiple reserve categories)
  const hasVirtualFolders = Array.from(folderIdToRescats.values()).some(
    (rescats) => rescats.size > 1
  );

  const uniqueFolderIds = [...new Set(validItems.map((item) => item.folderId))];

  const folderNames: string[] = [];

  uniqueFolderIds.forEach((folderId) => {
    const folder = treeData.find((node) => node.folderId === folderId && node.isFolder);
    const baseName = folder?.title || folder?.folderName;

    if (!baseName) return;

    const rescats = folderIdToRescats.get(folderId);

    if (hasVirtualFolders && rescats && rescats.size > 1) {
      // Virtual folders: same folderId with different reserve categories
      // Append reserve category to distinguish them
      rescats.forEach((rescat) => {
        folderNames.push(`${baseName} (${rescat})`);
      });
    } else {
      // Single folder or no virtual folder scenario
      folderNames.push(baseName);
    }
  });

  return folderNames;
}

/**
 * Calculates the primary and secondary forecast source folders based on selection mode and current state.
 *
 * @param selectedUserFolders - Array of selected user folder names
 * @param forecastMode - Current forecast mode (Trump or Compare)
 * @param specialFolderCount - Count of special folders selected (McDaniel, Novi, Working)
 * @param currentPrimaryFolder - Optional current primary folder to preserve
 * @param currentSettings - Current user forecast settings for preserving assignments
 * @param isSync - Flag indicating if sync mode is active
 * @param selectedFolderForSyncComparison - The single folder name to compare against synced forecasts (user or special folder)
 * @returns Object containing primary and secondary forecast source folder names
 */
export function calculateForecastSourceFolders(
  selectedUserFolders: string[],
  forecastMode: ForecastMode,
  specialFolderCount: number,
  currentPrimaryFolder: string | undefined,
  currentSettings:
    | {
        forecastSourceFolder: string | null;
        secondaryUserForecastSourceFolder: string | null;
      }
    | undefined,
  isSync: boolean,
  selectedFolderForSyncComparison: string | null = null
): {
  primaryForecastSourceFolder: string | null;
  secondaryForecastSourceFolder: string | null;
} {
  const userFolderCount = selectedUserFolders.length;
  const totalFolders = userFolderCount + specialFolderCount;

  let primaryForecastSourceFolder: string | null;
  let secondaryForecastSourceFolder: string | null = null;

  // Sync mode in Compare: primary is the synced-database placeholder, secondary is the
  // selected folder (user or special)
  if (isSync && forecastMode === ForecastMode.Compare) {
    primaryForecastSourceFolder = SYNC_DB_FOLDER_NAME;
    secondaryForecastSourceFolder = selectedFolderForSyncComparison;
    return { primaryForecastSourceFolder, secondaryForecastSourceFolder };
  }

  if (userFolderCount === 0) {
    // No user folders selected
    primaryForecastSourceFolder = null;
  } else if (userFolderCount === 1) {
    // Single user folder selected
    primaryForecastSourceFolder = selectedUserFolders[0];
  } else {
    // Multiple user folders selected
    if (
      forecastMode === ForecastMode.Compare &&
      totalFolders === 2 &&
      specialFolderCount === 0
    ) {
      // Compare mode with exactly 2 USER folders (no special folders)
      // Preserve current primary/secondary assignment if possible
      const currentPrimary = currentSettings?.forecastSourceFolder;
      const currentSecondary = currentSettings?.secondaryUserForecastSourceFolder;

      // If both current primary and secondary are in the selected folders, preserve the assignment
      if (
        currentPrimary &&
        selectedUserFolders.includes(currentPrimary) &&
        currentSecondary &&
        selectedUserFolders.includes(currentSecondary)
      ) {
        primaryForecastSourceFolder = currentPrimary;
        secondaryForecastSourceFolder = currentSecondary;
      } else {
        // Otherwise, assign based on the selection order or current state
        if (currentPrimary && selectedUserFolders.includes(currentPrimary)) {
          // Keep current primary if it's still selected
          primaryForecastSourceFolder = currentPrimary;
          secondaryForecastSourceFolder =
            selectedUserFolders.find((f) => f !== currentPrimary) || "";
        } else {
          // Assign new primary/secondary from selection order
          primaryForecastSourceFolder = selectedUserFolders[0];
          secondaryForecastSourceFolder = selectedUserFolders[1];
        }
      }
    } else {
      // Trump mode OR Compare with special folders OR != 2 folders
      // Use current primary folder if available, otherwise use first available
      primaryForecastSourceFolder =
        currentPrimaryFolder && selectedUserFolders.includes(currentPrimaryFolder)
          ? currentPrimaryFolder
          : selectedUserFolders[0];
    }
  }

  return { primaryForecastSourceFolder, secondaryForecastSourceFolder };
}
